import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Form,
  Modal,
} from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import logoDark from "../../../assets/images/anjal-gold-logo.png";
import logoLight from "../../../assets/images/anjal-gold-logo.png";
import {
  InvoiceData,
  InvoiceItem,
  Order,
  OrderProduct,
  OrderStatusHistoryItem,
} from "interface";
import {
  generateInvoicePDF,
  getOrderById,
  changeOrderAddress,
  cancelOrder,
  ContactInfo,
  getContactInfo1,
} from "api/apis";
import { toast } from "react-toastify";

const OrdersOverview = () => {
  const { id } = useParams<{ id?: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCurrency] = useState("₹");

  // Address change modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    address: "",
    firstName: "",
    lastName: "",
    phone: "",
    zipCode: "",
    state: "",
    country: "",
    city: "",
  });
  const [addressLoading, setAddressLoading] = useState(false);
    const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  
  // Cancel order modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    state: "",
  });
  const [shippingAddress, setShippingAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    state: "",
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
  });
  const [items, setItems] = useState([{ id: 1 }]);

  const STATUS_FLOW = [
    "Not processed",
    "Processing",
    "Shipped",
    "Delivered",
    "Cancelled",
  ];

  const STATUS_LABELS: { [key: string]: string } = {
    Not_processed: "Not processed",
    Processing: "Processing",
    Shipped: "Shipped",
    Delivered: "Delivered",
    Cancelled: "Cancelled",
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        const fetchedOrder = await getOrderById(id);
        console.log("Order: ", fetchedOrder);
        setOrder(fetchedOrder);

        // Set current address data
        const orderData: any = fetchedOrder; // Use any to bypass TypeScript checking temporarily

        if (fetchedOrder.address) {
          if (typeof fetchedOrder.address === "string") {
            setAddressData({
              address: fetchedOrder.address,
              firstName:
                orderData.firstName || fetchedOrder.user?.firstName || "",
              lastName: orderData.lastName || fetchedOrder.user?.lastName || "",
              phone: orderData.phone || "",
              zipCode: orderData.zipCode || "",
              state: orderData.state || "",
              country: orderData.country || "",
              city: orderData.city || "",
            });
          } else {
            // If address is an object
            const addressObj: any = fetchedOrder.address;
            setAddressData({
              address: addressObj.address || "",
              firstName:
                addressObj.firstName ||
                orderData.firstName ||
                fetchedOrder.user?.firstName ||
                "",
              lastName:
                addressObj.lastName ||
                orderData.lastName ||
                fetchedOrder.user?.lastName ||
                "",
              phone: addressObj.phone || orderData.phone || "",
              zipCode:
                addressObj.zipCode ||
                addressObj.zipcode ||
                orderData.zipCode ||
                "",
              state: addressObj.state || orderData.state || "",
              country: addressObj.country || orderData.country || "",
              city: addressObj.city || orderData.city || "",
            });
          }
        } else {
          // Use order fields directly
          setAddressData({
            address: orderData.address || "",
            firstName:
              orderData.firstName || fetchedOrder.user?.firstName || "",
            lastName: orderData.lastName || fetchedOrder.user?.lastName || "",
            phone: orderData.phone || "",
            zipCode: orderData.zipCode || "",
            state: orderData.state || "",
            country: orderData.country || "",
            city: orderData.city || "",
          });
        }

        // Set customer info from order
        if (fetchedOrder.user) {
          setCustomerInfo({
            name: `${fetchedOrder.user.firstName || ""} ${
              fetchedOrder.user.lastName || ""
            }`.trim(),
            email: fetchedOrder.user.email || "",
          });
        }

        // Set address info if available
        if (fetchedOrder.address && typeof fetchedOrder.address !== "string") {
          const addressObj: any = fetchedOrder.address;
          setBillingAddress({
            street: addressObj.address || "",
            city: addressObj.city || "",
            postalCode: addressObj.zipcode || "",
            state: addressObj.state || "",
          });
          setShippingAddress({
            street: addressObj.address || "",
            city: addressObj.city || "",
            postalCode: addressObj.zipcode || "",
            state: addressObj.state || "",
          });
        }

        // Set items from order products
        if (fetchedOrder.products) {
          setItems(
            fetchedOrder.products.map((_, index) => ({ id: index + 1 })),
          );
        }
      } catch (error) {
        console.error("Failed to fetch order:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  document.title = "Order Overview";
  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async (): Promise<void> => {
    try {
      setLoading(true);
      const contactData = await getContactInfo1();

      if (contactData) {
        setContactInfo(contactData);

        const companyAddress = [
          contactData.officeNo,
          contactData.floor,
          contactData.building,
          contactData.landmark,
          contactData.street,
          contactData.area,
          contactData.pincode,
        ]
          .filter(Boolean)
          .join(", ");
      }
    } catch (error: any) {
      console.error("Error fetching contact info:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch contact information");
      }
    } finally {
      setLoading(false);
    }
  };
  // Handle address data change
  const handleAddressDataChange = (field: string, value: string) => {
    setAddressData({
      ...addressData,
      [field]: value,
    });
  };

  // Handle address change
  const handleAddressChange = async () => {
    // Ensure address is a string
    const addressString =
      typeof addressData.address === "string"
        ? addressData.address
        : (addressData.address as any)?.address || "";

    if (!id || !addressString.trim()) {
      alert("Please enter a valid address");
      return;
    }

    setAddressLoading(true);
    try {
      const result = await changeOrderAddress(id, {
        ...addressData,
        address: addressString,
      });

      if (result.success) {
        alert(result.message);
        setShowAddressModal(false);

        // Refresh order data
        const updatedOrder = await getOrderById(id);
        setOrder(updatedOrder);

        // Update local address data
        const orderData: any = updatedOrder;
        const updatedAddress = orderData.address;

        // Extract address string from either string or object
        let addressValue = "";
        if (typeof updatedAddress === "string") {
          addressValue = updatedAddress;
        } else if (updatedAddress && typeof updatedAddress === "object") {
          addressValue = updatedAddress.address || "";
        }

        setAddressData({
          address: addressValue || addressData.address,
          firstName: orderData.firstName || addressData.firstName,
          lastName: orderData.lastName || addressData.lastName,
          phone: String(orderData.phone || addressData.phone),
          zipCode: orderData.zipCode || addressData.zipCode,
          state: orderData.state || addressData.state,
          country: orderData.country || addressData.country,
          city: orderData.city || addressData.city,
        });
      } else {
        alert(result.message);
      }
    } catch (error: any) {
      console.error("Failed to update address:", error);
      alert(error.message || "Failed to update address");
    } finally {
      setAddressLoading(false);
    }
  };

  // Handle order cancellation
  const handleCancelOrder = async () => {
    if (!id) return;

    setCancelLoading(true);
    try {
      const result = await cancelOrder(id, cancellationReason);

      if (result.success) {
        alert(result.message);
        setShowCancelModal(false);
        setCancellationReason("");

        // Refresh order data
        const updatedOrder = await getOrderById(id);
        setOrder(updatedOrder);
      } else {
        alert(result.message || "Failed to cancel order");
      }
    } catch (error: any) {
      console.error("Failed to cancel order:", error);
      alert(error.message || "Failed to cancel order");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDownloadInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!order) {
      console.error("No order data available");
      return;
    }

    try {
      // Prepare products for the invoice
      const products = order.products.map((productItem: OrderProduct) => {
        const product = productItem.product;

        // Extract attributes for the PDF
        const attributes = [];
        if (product?.karat) {
          attributes.push({ label: "Karat", value: product.karat });
        }
        if (productItem.weight || product?.weightInGrams) {
          attributes.push({
            label: "Weight",
            value: (
              productItem.weight ||
              product?.weightInGrams ||
              0
            ).toString(),
          });
        }

        return {
          productSnapshot: {
            name: product?.name || "Product",
            description: "",
          },
          variantSku:
            productItem.variantSku ||
            `SKU-${productItem._id?.slice(-6) || "000000"}`,
          attributes: attributes,
          weight: productItem.weight || product?.weightInGrams || 0,
          purchasePrice: productItem.purchasePrice || 0,
          quantity: productItem.quantity || 1,
          // Calculate total correctly
          total: (productItem.purchasePrice || 0) * (productItem.quantity || 1),
        };
      });

      // Extract price breakdown from order
      const priceBreakdown = [];
      let subtotal = 0;
      let tax = 0;
      let discount = 0;
      let grandTotal = 0;
      let finalTotal = 0;

      if (order.priceBreakdown) {
        // Convert priceBreakdown object to array
        Object.entries(order.priceBreakdown).forEach(([key, item]) => {
          if (item.show !== false) {
            priceBreakdown.push({
              label: item.label,
              value: item.value,
              type: item.type,
            });

            // Extract specific values
            if (key === "subtotal_base_price_") {
              subtotal = item.value;
            } else if (key === "total_tax") {
              tax = item.value;
            } else if (key.includes("discount") || key.includes("coupon")) {
              discount += Math.abs(item.value);
            } else if (key === "grand_total") {
              grandTotal = item.value;
            } else if (key === "final_total") {
              finalTotal = item.value;
            }
          }
        });
      } else {
        // Fallback calculations
        subtotal = order.products.reduce(
          (sum, item) => sum + (item.purchasePrice || 0) * (item.quantity || 1),
          0,
        );
        tax = order.chargesBreakdown?.charges?.tax?.amount || 0;
        discount = order.coupon?.discountAmount || 0;
        finalTotal = order.total || 0;

        priceBreakdown.push(
          { label: "Subtotal", value: subtotal, type: "subtotal" },
          { label: "Tax", value: tax, type: "tax" },
        );

        if (discount > 0) {
          priceBreakdown.push({
            label: "Discount",
            value: -discount,
            type: "discount",
          });
        }

        priceBreakdown.push({
          label: "Total Amount",
          value: finalTotal,
          type: "grandTotal",
        });
      }

      // Get address info
      const address = order.address;
      const addressObj: any = address;

      // Prepare invoice data
      const invoiceData = {
        _id: order._id,
        invoiceNumber: `INV-${order._id.slice(-8).toUpperCase()}`,
        date: moment(order.created).format("D MMM, YYYY"),
        dueDate: moment(order.created).add(30, "days").format("D MMM, YYYY"),
        paymentStatus: order.paymentStatus || "Paid",

        // Company info
        companyName: "Anjal Gold",
        companyAddress: contactInfo?.fullAddress,
        companyPostalCode: contactInfo?.pincode,
        gstin: contactInfo?.gstin,
        companyEmail: contactInfo?.email,
        companyWebsite: "www.anjalgold.com",
        companyPhone: contactInfo?.phone,

        // Customer info
        firstName: addressObj?.firstName || order.user?.firstName || "Customer",
        lastName: addressObj?.lastName || order.user?.lastName || "",
        address: addressObj?.address || "",
        city: addressObj?.city || "",
        zipCode: addressObj?.zipCode || addressObj?.zipcode || "",
        country: addressObj?.country || "India",
        phone: addressObj?.phone || order.phone || "",
        email: order.user?.email || "",

        // Products
        products: products,

        // Price breakdown
        priceBreakdown: priceBreakdown,

        // Individual totals for backup
        subtotal: subtotal,
        totalTax: tax,
        total: finalTotal || grandTotal || order.total,

        // Payment information
        paymentMethod: order.paymentMethod || "Cash",
        paymentDetails: order.paymentMethod === "card" ? "Online" : "",
        razorpayPaymentId: order.paymentId?.code || "",

        // Currency
        currency: "₹",

        // Notes
        orderNotes:
          order.orderNotes ||
          "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",
      };

      console.log("Final invoice data being sent:", {
        invoiceNumber: invoiceData.invoiceNumber,
        subtotal: invoiceData.subtotal,
        tax: invoiceData.totalTax,
        total: invoiceData.total,
        priceBreakdown: invoiceData.priceBreakdown,
      });

      // Generate the PDF invoice
      await generateInvoicePDF(invoiceData);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      // Consider adding user feedback here
      // toast.error("Failed to generate invoice. Please try again.");
    }
  };

  // Helper function to get order field with proper type handling
  const getOrderField = (field: string): string => {
    const orderData: any = order;
    return String(orderData?.[field] || "");
  };

  // Helper function to safely get address string
  const getAddressString = (): string => {
    if (!order?.address) return "";

    if (typeof order.address === "string") {
      return order.address;
    } else {
      const addressObj: any = order.address;
      return addressObj?.address || "";
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumb title="Overview" pageTitle="Orders" />

          <Row className="mb-4 align-items-center">
            <Col>
              <h6 className="fs-18 mb-0">Order ID: {id}</h6>
            </Col>
            <Col className="col text-end">
              <Button variant="primary" onClick={handleDownloadInvoice}>
                <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                Download Invoice
              </Button>
            </Col>
          </Row>

          <Row>
            <Col xxl={4} lg={6}>
              <div className="card bg-success bg-opacity-10 border-0">
                <div className="card-body">
                  <div className="d-flex gap-3">
                    <div className="flex-grow-1">
                      <h6 className="fs-18 mb-3">Customer Info</h6>
                      <p className="mb-0 fw-medium">
                        {order?.firstName || order?.user?.firstName}{" "}
                        {order?.lastName || order?.user?.lastName}
                      </p>
                      <p className="mb-1">{order?.user?.email}</p>
                      <p className="mb-0">{String(order?.phone || "")}</p>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                        <i className="ph-user-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xxl={4} lg={6}>
              <div className="card bg-primary bg-opacity-10 border-0">
                <div className="card-body">
                  <div className="d-flex gap-3">
                    <div className="flex-grow-1">
                      <h6 className="fs-18 mb-3">Shipping Address</h6>
                      {typeof order?.address === "string" ? (
                        <p className="mb-0">{order.address}</p>
                      ) : (
                        <>
                          <p className="mb-0">
                            {getOrderField("firstName") ||
                              (order?.address as any)?.firstName ||
                              ""}{" "}
                            {getOrderField("lastName") ||
                              (order?.address as any)?.lastName ||
                              ""}
                          </p>
                          <p className="mb-0">
                            {String(
                              getOrderField("phone") ||
                                (order?.address as any)?.phone ||
                                "",
                            )}
                          </p>
                          <p className="mb-0">
                            {(order?.address as any)?.address || ""}
                          </p>
                          <p className="mb-0">
                            {getOrderField("zipCode") ||
                              (order?.address as any)?.zipcode ||
                              ""}
                          </p>
                          <p className="mb-1">
                            {getOrderField("city") ||
                              (order?.address as any)?.city ||
                              ""}
                          </p>
                          <p className="mb-0">
                            {getOrderField("state") ||
                              (order?.address as any)?.state ||
                              ""}
                          </p>
                          <p className="mb-0">
                            {getOrderField("country") ||
                              (order?.address as any)?.country ||
                              ""}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ph-map-pin"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xxl={4} lg={6}>
              <div className="card card-primary overflow-hidden">
                <div className="position-absolute end-0 start-0 bottom-0 z-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    // xmlns:xlink="http://www.w3.org/1999/xlink"
                    width="400"
                    height="250"
                    preserveAspectRatio="none"
                    viewBox="0 0 400 250"
                  >
                    <g mask='url("#SvgjsMask1551")' fill="none">
                      <path
                        d="M306 65L446 -75"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M399 2L315 86"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M83 77L256 -96"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M281 212L460 33"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M257 62L76 243"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M305 123L214 214"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M327 222L440 109"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M287 109L362 34"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M259 194L332 121"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M376 186L240 322"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M308 153L123 338"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M218 62L285 -5"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                    </g>
                    <defs>
                      <mask id="SvgjsMask1551">
                        <rect width="400" height="250" fill="#ffffff"></rect>
                      </mask>
                      <linearGradient
                        x1="100%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        id="SvgjsLinearGradient1558"
                      >
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                        id="SvgjsLinearGradient1559"
                      >
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="card-body position-relative">
                  <h5 className="card-title fw-medium">Add New Orders</h5>
                  <p className="text-white-75">
                    Orders are one of the main building blocks of creating an an
                    ad campaign in Ad Manager.
                  </p>
                  <Link
                    to="/orders-list-view"
                    className="link-light text-white"
                  >
                    Create Order <i className="ph-arrow-right align-middle"></i>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xxl={9}>
              <Card>
                <div className="card-body">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-nowrap mb-0">
                      <thead className="text-muted table-light">
                        <tr>
                          <th scope="col">Product ID</th>
                          <th scope="col">Product Name</th>
                          {/* <th scope="col">Base Price</th> */}
                          <th scope="col">Karat</th>
                          <th scope="col">Weight(in grams)</th>
                          <th scope="col">Quantity</th>
                          <th scope="col" className="text-end">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {order?.products.map(
                          (item: OrderProduct, index: number) => (
                            <tr key={index}>
                              <td>
                                <Link
                                  to="#!"
                                  className="fw-medium link-primary"
                                >
                                  #{item?.variantSku}
                                </Link>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0 me-2">
                                    <img
                                      src={
                                        item?.product?.images?.[0] ??
                                        "/placeholder.jpg"
                                      }
                                      alt=""
                                      className="avatar-xs rounded-circle"
                                    />
                                  </div>
                                  <div className="flex-grow-1">
                                    {item.product?.name}
                                  </div>
                                </div>
                              </td>
                              {/* <td>
                                <span className="text-secondary">
                                  ₹{item?.purchasePrice}
                                </span>
                              </td> */}
                              <td>{item.product.karat}</td>
                              <td>{item.weight} g</td>
                              <td>{item.quantity} PCS</td>
                              {/* <td className="text-end">₹{item.basePrice}</td> */}
                              <td className="text-end">
                                ₹
                                {Number(
                                  order?.priceBreakdown?.final_total?.value ??
                                    0,
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          ),
                        )}

                        <tr>
                          <td colSpan={7} className="p-0">
                            <table className="table table-borderless mb-0">
                              <tbody>
                                {order?.priceBreakdown &&
                                  Object.entries(order.priceBreakdown).map(
                                    ([key, breakdown]) => (
                                      <tr key={key}>
                                        <td className="align-middle">
                                          {breakdown.label}
                                          {key.startsWith("coupon_discount") &&
                                            order?.coupon?.code && (
                                              <span
                                                style={{
                                                  backgroundColor: "#f0f0f0",
                                                  borderRadius: "12px",
                                                  padding: "2px 8px",
                                                  fontSize: "0.85rem",
                                                  marginLeft: "8px",
                                                  color: "#333",
                                                  display: "inline-block",
                                                }}
                                              >
                                                {order.coupon.code}
                                              </span>
                                            )}
                                        </td>
                                        <td
                                          className={`text-end ${
                                            breakdown.type === "total" ||
                                            breakdown.type === "grandTotal"
                                              ? "fw-semibold"
                                              : ""
                                          }`}
                                        >
                                          ₹
                                          {Number(
                                            breakdown.value,
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                          })}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xxl={3}>
              <Row>
                <Col lg={12}>
                  <Card>
                    <div className="card-header d-flex align-items-center">
                      <h5 className="card-title flex-grow-1 mb-0">
                        Logistics Details
                      </h5>
                      {/* <div className="flex-shrink-0">
                        <Link to="#track-order" className="btn btn-sm btn-info">
                          Track Order
                        </Link>
                      </div> */}
                    </div>
                    <div className="card-body">
                      <div className="text-center">
                        <i className="bi bi-truck fs-1"></i>
                        {/* <h5 className="fs-18">Anjal Logistics</h5>
                        <p className="mb-0">ID: MFDS1400457854</p> */}
                        <p className="mb-0">
                          Payment Mode : {order?.paymentMethod}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Col>
                {/* <Col lg={12}>
                  <Card>
                    <div className="card-header">
                      <h5 className="card-title mb-0">Payment Detail:</h5>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless align-middle description-table mb-0">
                          <tbody>
                            <tr>
                              <td>Transactions:</td>
                              <td>
                                <span className="fw-medium">
                                  #TNR124561278124
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Payment Method</td>
                              <td>
                                <span className="fw-medium">
                                  {order?.paymentMethod}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Card Number:</td>
                              <td>
                                <span className="fw-medium">
                                  XXXX XXXX XXXX 3028
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Card Holder Name</td>
                              <td>
                                <span className="fw-medium">Vinod Kumar</span>
                              </td>
                            </tr>
                            <tr>
                              <td>Total Amount</td>
                              <td>
                                <span className="fw-medium">
                                  ₹{order?.total}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                </Col> */}
              </Row>
            </Col>

            {order?.status && (
              <Col lg={12}>
                <Card id="track-order">
                  <div className="card-header d-flex align-items-center gap-3">
                    <h5 className="card-title flex-grow-1 mb-0">
                      Order Status
                    </h5>
                    <div className="flex-shrink-0">
                      <Button
                        variant="soft-primary"
                        size="sm"
                        className="btn btn-sm mt-2 mt-sm-0 me-1"
                        onClick={() => setShowAddressModal(true)}
                        disabled={
                          order.status === "Cancelled" ||
                          order.status === "Shipped" ||
                          order.status === "Delivered"
                        }
                      >
                        <i className="ri-map-pin-line align-bottom me-1"></i>{" "}
                        Change Address
                      </Button>
                      <Button
                        variant="soft-danger"
                        size="sm"
                        className="btn btn-sm mt-2 mt-sm-0"
                        onClick={() => setShowCancelModal(true)}
                        disabled={
                          order.status === "Cancelled" ||
                          order.status === "Shipped" ||
                          order.status === "Delivered"
                        }
                      >
                        <i className="mdi mdi-archive-remove-outline align-bottom me-1"></i>{" "}
                        Cancel Order
                      </Button>
                    </div>
                  </div>

                  <div className="card-body">
                    <Row
                      className="d-flex order-tracking-wrapper flex-lg-row flex-column align-items-lg-stretch align-items-center"
                      style={{ position: "relative" }}
                    >
                      {STATUS_FLOW.map((statusKey, index) => {
                        const currentStatusIndex = STATUS_FLOW.indexOf(
                          order.status,
                        );
                        const isCompleted = index <= currentStatusIndex;
                        const historyItem = order.statusHistory?.find(
                          (s: OrderStatusHistoryItem) => s.status === statusKey,
                        );

                        return (
                          <Col
                            lg={2}
                            xs={12}
                            key={statusKey}
                            className={`order-tracking d-flex flex-column align-items-center position-relative text-center ${
                              isCompleted ? "completed" : ""
                            }`}
                          >
                            <span className="is-complete"></span>
                            <div className="card mt-3 mb-0 w-100">
                              <div className="card-body">
                                <h6 className="fs-17">
                                  {STATUS_LABELS[statusKey] || statusKey}
                                </h6>
                                <p className="text-muted fs-15 mb-0">
                                  {historyItem
                                    ? moment(historyItem.changedAt).format(
                                        "ddd, DD MMM, YYYY",
                                      )
                                    : isCompleted
                                      ? "Completed"
                                      : "Pending"}
                                </p>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      {/* Address Change Modal */}
      <Modal
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Shipping Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.firstName}
                    onChange={(e) =>
                      handleAddressDataChange("firstName", e.target.value)
                    }
                    placeholder="Enter first name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.lastName}
                    onChange={(e) =>
                      handleAddressDataChange("lastName", e.target.value)
                    }
                    placeholder="Enter last name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="text"
                value={addressData.phone}
                onChange={(e) =>
                  handleAddressDataChange("phone", e.target.value)
                }
                placeholder="Enter phone number"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={
                  typeof addressData.address === "string"
                    ? addressData.address
                    : ""
                }
                onChange={(e) =>
                  handleAddressDataChange("address", e.target.value)
                }
                placeholder="Enter complete address (street, building, area)"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.city}
                    onChange={(e) =>
                      handleAddressDataChange("city", e.target.value)
                    }
                    placeholder="Enter city"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.state}
                    onChange={(e) =>
                      handleAddressDataChange("state", e.target.value)
                    }
                    placeholder="Enter state"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country *</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.country}
                    onChange={(e) =>
                      handleAddressDataChange("country", e.target.value)
                    }
                    placeholder="Enter country"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ZIP Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressData.zipCode}
                    onChange={(e) =>
                      handleAddressDataChange("zipCode", e.target.value)
                    }
                    placeholder="Enter ZIP code"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="text-muted mb-3">
              <small>* Required fields</small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddressModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddressChange}
            disabled={
              addressLoading ||
              !(
                typeof addressData.address === "string"
                  ? addressData.address
                  : ""
              ).trim() ||
              !addressData.firstName.trim() ||
              !addressData.phone.trim()
            }
          >
            {addressLoading ? "Updating..." : "Update Address"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Order Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger mb-3">
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Reason for Cancellation (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancellation (optional)"
              />
              <Form.Text className="text-muted">
                This will help us improve our service.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Keep Order
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelOrder}
            disabled={cancelLoading}
          >
            {cancelLoading ? "Cancelling..." : "Yes, Cancel Order"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default OrdersOverview;
